import { z } from "zod";
import { localizedTextSchema } from "./intake-template";

// TES-1067 (TES-1065 points 2–3) — the two "Stöd → Kost" courses Emelie made
// launch-near core content on 2026-10-02, how they are recommended, and the
// protein target the first of them shows.
//
// The source document is "04 — Kurser om mat — kurskällor och konvertering"
// (Drive 1OS8VA0K7ZJY0pwYLk-y25wcUkat3zZfXlNrTPW7tQz0); its decisions quoted
// below are verbatim.

export const PROTEIN_COURSE_KEY = "course.nutrition.protein_and_muscle";
export const CONSTIPATION_COURSE_KEY =
  "course.nutrition.constipation_during_treatment";
// "Lektion 2 — Ditt proteinmål": the lesson the personal target belongs to.
export const PROTEIN_TARGET_LESSON_KEY = `${PROTEIN_COURSE_KEY}.lesson_02`;

// Her recommended order: the base course, protein, constipation, sugar,
// then the other situation- and women-specific courses.
// "Rekommenderad ordning i Stöd → Kost: 1. Mat under behandlingen."
// "2. Protein och muskelmassa under medicinsk viktminskning."
// "3. Förstoppning under medicinsk viktminskning."
// (TES-1107: the titles as renamed 2026-10-05.)
// "4. Socker, sötsug och hållbara val."
// "Därefter övriga situations- och kvinnospecifika kurser."
//
// The sugar course is not imported yet; naming its key here costs nothing and
// places it correctly the day it is.
export const NUTRITION_COURSE_ORDER = [
  "course.nutrition.food_during_treatment",
  PROTEIN_COURSE_KEY,
  CONSTIPATION_COURSE_KEY,
  "course.nutrition.sugar_cravings_sustainable_choices",
] as const;

// Courses in that list first, in that order; everything else after, by key.
export function compareNutritionCourseKeys(a: string, b: string): number {
  const order = NUTRITION_COURSE_ORDER as readonly string[];
  const ia = order.indexOf(a);
  const ib = order.indexOf(b);
  if (ia >= 0 || ib >= 0) {
    if (ia < 0) return 1;
    if (ib < 0) return -1;
    return ia - ib;
  }
  return a.localeCompare(b);
}

// ---- Recommendations -------------------------------------------------------
//
// "Protein och muskelmassa rekommenderas proaktivt en gång i nära anslutning"
// "till behandlingsstart."
// "Förstoppning ligger sökbar och synlig från start men rekommenderas"
// "särskilt när patienten rapporterar förstoppning i en strukturerad"
// "incheckning eller när coachen aktivt rekommenderar kursen."
// "En rekommendation får avfärdas utan att kursen försvinner ur biblioteket."
// "Kursrekommendationen och den medicinska säkerhetssignalen är separata."
//
// The last line is structural here: a recommendation is its own row, read by
// its own endpoint, and never ranked against Home's "Vad händer just nu"
// card — the one that carries an acute risk.
export const RECOMMENDABLE_COURSE_KEYS = [
  PROTEIN_COURSE_KEY,
  CONSTIPATION_COURSE_KEY,
] as const;
export const recommendableCourseKeySchema = z.enum(RECOMMENDABLE_COURSE_KEYS);
export type RecommendableCourseKey = z.infer<
  typeof recommendableCourseKeySchema
>;

export const COURSE_RECOMMENDATION_REASONS = [
  // Her journey reached treatment start (treatment_started or later).
  "treatment_start",
  // A structured check-in (F-04 or F-05) reported constipation.
  "constipation_reported",
  // Her coach (or doctor) recommended it.
  "coach",
] as const;
export const courseRecommendationReasonSchema = z.enum(
  COURSE_RECOMMENDATION_REASONS,
);
export type CourseRecommendationReason = z.infer<
  typeof courseRecommendationReasonSchema
>;

// The patient's view: one entry per course with at least one open
// recommendation, only for courses whose overview is published.
export const patientCourseRecommendationSchema = z.object({
  courseKey: recommendableCourseKeySchema,
  title: localizedTextSchema,
  reasons: z.array(courseRecommendationReasonSchema),
  // The newest open recommendation's time.
  recommendedAt: z.string(),
});
export type PatientCourseRecommendation = z.infer<
  typeof patientCourseRecommendationSchema
>;

// The care team's view: every row, dismissed ones included, newest first.
export const courseRecommendationRowSchema = z.object({
  id: z.string(),
  courseKey: recommendableCourseKeySchema,
  reason: courseRecommendationReasonSchema,
  recommendedByEmployeeId: z.string().nullable(),
  createdAt: z.string(),
  dismissedAt: z.string().nullable(),
});
export type CourseRecommendationRow = z.infer<
  typeof courseRecommendationRowSchema
>;

export const recommendCourseSchema = z.object({
  courseKey: recommendableCourseKeySchema,
});
export type RecommendCourse = z.infer<typeof recommendCourseSchema>;

// ---- Protein target --------------------------------------------------------
//
// "Följande regel är aktiv och får implementeras:"
// "• Generellt proteinriktmärke: 1,2 gram protein per kilo referensvikt och dag."
// "• Referensvikt: patientens registrerade målvikt eller annan vikt som"
// "vårdteamet uttryckligen har fastställt som referens."
// "• Aktuell kroppsvikt får inte användas som tyst standard vid obesitas."
// "• Individuellt högre mål upp till 1,6 gram per kilo referensvikt och dag"
// "kräver individuell bedömning."
// "• Vid njursjukdom, relevant leversjukdom eller annan medicinsk"
// "förutsättning som kräver individualisering visas Personligt proteinmål"
// "behöver bedömas i stället för ett automatiskt tal."
export const PROTEIN_GRAMS_PER_KG_DEFAULT = 1.2;
export const PROTEIN_GRAMS_PER_KG_MAX = 1.6;

// What the care team has set explicitly. Every field is optional on its own:
//   referenceWeightKg            — a weight the care team set explicitly;
//                                  wins over the goal weight.
//   gramsPerKg                   — the outcome of an individual assessment,
//                                  1.2–1.6. Setting it IS that assessment, so
//                                  it also lifts "behöver bedömas".
//   individualAssessmentRequired — another condition needing an individual
//                                  assessment, one F-02 cannot see.
export const setProteinTargetSchema = z.object({
  referenceWeightKg: z.number().min(20).max(500).nullable(),
  gramsPerKg: z
    .number()
    .min(PROTEIN_GRAMS_PER_KG_DEFAULT)
    .max(PROTEIN_GRAMS_PER_KG_MAX)
    .nullable(),
  individualAssessmentRequired: z.boolean(),
});
export type SetProteinTarget = z.infer<typeof setProteinTargetSchema>;

// Why the automatic number is withheld.
export const PROTEIN_ASSESSMENT_CAUSES = [
  // F-02 med_kidney answered yes or unsure.
  "kidney",
  // F-02 med_liver (liver disease other than fatty liver), yes or unsure.
  "liver",
  // The care team marked it.
  "care_team",
] as const;
export type ProteinAssessmentCause = (typeof PROTEIN_ASSESSMENT_CAUSES)[number];

export const proteinTargetSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("computed"),
    gramsPerDay: z.number().int(),
    gramsPerKg: z.number(),
    referenceWeightKg: z.number(),
    referenceSource: z.enum(["goal_weight", "care_team"]),
  }),
  // Show "Personligt proteinmål behöver bedömas", never a number.
  z.object({
    status: z.literal("needs_assessment"),
    causes: z.array(z.enum(PROTEIN_ASSESSMENT_CAUSES)),
  }),
  // No goal weight and no care-team weight: no number. Current weight is
  // never the fallback.
  z.object({ status: z.literal("no_reference_weight") }),
]);
export type ProteinTarget = z.infer<typeof proteinTargetSchema>;

export interface ProteinTargetInput {
  // Patient.goalWeightKg — her registered goal (she or the doctor set it).
  goalWeightKg: number | null;
  careTeam: SetProteinTarget | null;
  // From her latest F-02.
  f02Causes: ReadonlyArray<"kidney" | "liver">;
}

// The rule, as one pure function. There is deliberately no current-weight
// input: "Aktuell kroppsvikt får inte användas som tyst standard".
export function computeProteinTarget(input: ProteinTargetInput): ProteinTarget {
  const careTeam = input.careTeam;
  const assessed = careTeam?.gramsPerKg != null;
  const causes: ProteinAssessmentCause[] = [...input.f02Causes];
  if (careTeam?.individualAssessmentRequired) causes.push("care_team");
  if (causes.length > 0 && !assessed) {
    return { status: "needs_assessment", causes };
  }
  const careTeamWeight = careTeam?.referenceWeightKg ?? null;
  const referenceWeightKg = careTeamWeight ?? input.goalWeightKg;
  if (referenceWeightKg == null) return { status: "no_reference_weight" };
  const gramsPerKg = careTeam?.gramsPerKg ?? PROTEIN_GRAMS_PER_KG_DEFAULT;
  return {
    status: "computed",
    gramsPerDay: Math.round(referenceWeightKg * gramsPerKg),
    gramsPerKg,
    referenceWeightKg,
    referenceSource: careTeamWeight != null ? "care_team" : "goal_weight",
  };
}

// The care team's read: the computed target plus the setting behind it.
export const proteinTargetSettingSchema = setProteinTargetSchema.extend({
  setByEmployeeId: z.string(),
  createdAt: z.string(),
});
export type ProteinTargetSetting = z.infer<typeof proteinTargetSettingSchema>;

export const employeeProteinTargetSchema = z.object({
  target: proteinTargetSchema,
  goalWeightKg: z.number().nullable(),
  setting: proteinTargetSettingSchema.nullable(),
});
export type EmployeeProteinTarget = z.infer<typeof employeeProteinTargetSchema>;
